import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_CONFIG } from '../config/api';
import { queryClient } from '../config/queryClient';
import analyticsService from './analyticsService';

// Types
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  data: {
    user: {
      id: string;
      email: string;
      role: string;
      isActive: boolean;
      createdAt: string;
      avatarUrl?: string | null;
      language?: 'en' | 'es';
      theme?: 'light' | 'dark';
      impersonatedBy?: string;
      impersonatorEmail?: string;
    };
    accessToken: string;
    refreshToken: string;
    analyticsSessionId?: string;
  };
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export interface ApiError {
  success: false;
  message: string;
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

// Storage keys
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  USER_DATA: 'userData',
} as const;

class AuthService {
  private config = API_CONFIG;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private userData: any = null;
  private isRefreshing = false;
  private refreshSubscribers: Array<(token: string) => void> = [];

  // Initialize auth service
  async initialize(): Promise<void> {
    try {
      // Load tokens from storage
      this.accessToken = await this.getFromStorage(STORAGE_KEYS.ACCESS_TOKEN);
      this.refreshToken = await this.getFromStorage(STORAGE_KEYS.REFRESH_TOKEN);
      this.userData = await this.getFromStorage(STORAGE_KEYS.USER_DATA);
      this.mergeImpersonationFromTokenIntoUserData();
      if (this.userData) {
        await this.saveToStorage(STORAGE_KEYS.USER_DATA, this.userData);
      }
    } catch (error) {
      console.error('Error initializing auth service:', error);
    }
  }

  // Login user
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      console.log('🔐 Attempting login for:', credentials.email);
      
      const response = await fetch(`${this.config.BASE_URL}${this.config.ENDPOINTS.AUTH.LOGIN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      console.log('🔐 Login response status:', response.status);

      const data = await response.json();
      console.log('🔐 Login response data:', data);

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      if (data.success) {
        console.log('🔐 Login successful, storing tokens');
        await this.persistSession(data.data.accessToken, data.data.refreshToken, data.data.user);
        if (data.data.analyticsSessionId) {
          await analyticsService.setSessionId(data.data.analyticsSessionId);
        }
        console.log('🔐 Tokens stored successfully');
      }

      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // Forgot password
  async forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(`${this.config.BASE_URL}${this.config.ENDPOINTS.AUTH.FORGOT_PASSWORD}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send reset email');
      }

      return data;
    } catch (error) {
      console.error('Forgot password error:', error);
      throw error;
    }
  }

  // Reset password
  async resetPassword(token: string, password: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(`${this.config.BASE_URL}${this.config.ENDPOINTS.AUTH.RESET_PASSWORD}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to reset password');
      }

      return data;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  }

  // Logout user
  async logout(): Promise<void> {
    const accessToken = this.accessToken;
    const analyticsSessionId = analyticsService.getSessionId();
    try {
      try {
        await analyticsService.flush();
      } catch (error) {
        console.error('Analytics flush before logout failed:', error);
      }

      const sessionIdToClose = analyticsService.getSessionId() ?? analyticsSessionId;
      if (accessToken) {
        await fetch(`${this.config.BASE_URL}${this.config.ENDPOINTS.AUTH.LOGOUT}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'X-Client-Platform': Platform.OS === 'web' ? 'web' : Platform.OS === 'ios' ? 'ios' : 'android',
          },
          body: JSON.stringify({
            analyticsSessionId: sessionIdToClose ?? undefined,
          }),
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      await analyticsService.clearSession();
      this.clearAuthData();
    }
  }

  // Refresh access token with queue system
  async refreshAccessToken(): Promise<string | null> {
    if (this.isRefreshing) {
      // If already refreshing, wait for it to complete
      return new Promise((resolve) => {
        this.refreshSubscribers.push((token) => {
          resolve(token);
        });
      });
    }

    this.isRefreshing = true;

    try {
      if (!this.refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${this.config.BASE_URL}${this.config.ENDPOINTS.AUTH.REFRESH}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Token refresh failed');
      }

      if (data.success) {
        this.accessToken = data.data.accessToken;
        this.refreshToken = data.data.refreshToken;

        await this.saveToStorage(STORAGE_KEYS.ACCESS_TOKEN, this.accessToken);
        await this.saveToStorage(STORAGE_KEYS.REFRESH_TOKEN, this.refreshToken);

        this.mergeImpersonationFromTokenIntoUserData();
        if (this.userData) {
          await this.saveToStorage(STORAGE_KEYS.USER_DATA, this.userData);
        }

        // Notify all subscribers
        this.refreshSubscribers.forEach((callback) => callback(this.accessToken!));
        this.refreshSubscribers = [];

        return this.accessToken;
      }

      return null;
    } catch (error) {
      console.error('Token refresh error:', error);
      this.clearAuthData();
      return null;
    } finally {
      this.isRefreshing = false;
    }
  }

  // Get authenticated request headers
  async getAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    return headers;
  }

  // Make authenticated request with automatic token refresh
  async makeAuthenticatedRequest<T>(
    url: string,
    options: RequestInit = {}
  ): Promise<T> {
    try {
      // Get valid access token (refresh if needed)
      const validToken = await this.getValidAccessToken();
      if (!validToken) {
        this.clearAuthData();
        throw new Error('Authentication required');
      }

      // Add auth headers with valid token
      const requestOptions: RequestInit = {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${validToken}`,
          ...options.headers,
        },
      };

      const response = await fetch(url, requestOptions);

      if (response.status === 401) {
        // Token still invalid after refresh, clear auth data
        console.log('🚫 Token refresh failed, logging out...');
        this.clearAuthData();
        throw new Error('Authentication required');
      }

      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (typeof errorData.error === 'string') {
            errorMessage = errorData.error;
          } else if (
            errorData.error &&
            typeof errorData.error === 'object' &&
            typeof errorData.error.message === 'string'
          ) {
            errorMessage = errorData.error.message;
          }
        } catch (e) {
          // If response is not JSON, use default message
        }
        throw new Error(errorMessage);
      }

      return response.json();
    } catch (error) {
      console.error('Authenticated request error:', error);
      throw error;
    }
  }

  // Get current user
  getCurrentUser(): any {
    return this.userData;
  }

  // Get user profile with full name from database
  async getUserProfile(): Promise<any> {
    try {
      const response = await this.makeAuthenticatedRequest<{ success: boolean; data: any }>(
        `${this.config.BASE_URL}/api/auth/profile`
      );
      
      if (response.success) {
        // Update stored user data with fresh profile info
        this.userData = response.data;
        this.mergeImpersonationFromTokenIntoUserData();
        await this.saveToStorage(STORAGE_KEYS.USER_DATA, this.userData);
        return response.data;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  }

  // Check if user is authenticated (without making HTTP requests)
  isAuthenticated(): boolean {
    return !!(this.accessToken && this.userData);
  }

  // Check if access token is expired (without making HTTP requests)
  private isTokenExpired(token: string): boolean {
    try {
      console.log('🔄 Token:', token);
      // Simple base64 decode for React Native
      const base64Payload = token.split('.')[1];
      const decodedPayload = this.base64Decode(base64Payload);
      const payload = JSON.parse(decodedPayload);
      console.log('🔄 Payload:', payload);
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      console.log('🔄 Error:', error);
      return true; // If we can't parse the token, consider it expired
    }
  }

  // Simple base64 decode for React Native
  private base64Decode(str: string): string {
    try {
      // Add padding if needed
      const paddedStr = str + '==='.slice((str.length + 3) % 4);
      
      // Convert base64 to binary
      const binary = paddedStr.replace(/[^A-Za-z0-9+/]/g, '');
      let result = '';
      
      for (let i = 0; i < binary.length; i += 4) {
        const chunk = binary.slice(i, i + 4);
        const bits = chunk.split('').map(c => {
          const index = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'.indexOf(c);
          return index >= 0 ? index.toString(2).padStart(6, '0') : '';
        }).join('');
        
        for (let j = 0; j < bits.length; j += 8) {
          const byte = bits.slice(j, j + 8);
          if (byte.length === 8) {
            const charCode = parseInt(byte, 2);
            if (charCode > 0) {
              result += String.fromCharCode(charCode);
            }
          }
        }
      }
      
      return result;
    } catch (error) {
      console.error('Base64 decode error:', error);
      return '';
    }
  }

  // Get valid access token (refresh if needed)
  async getValidAccessToken(): Promise<string | null> {
    if (!this.accessToken) {
      return null;
    }
    console.log('🔄 Access token:', this.accessToken);
    // Check if token is expired
    if (this.isTokenExpired(this.accessToken)) {
      console.log('🔄 Access token expired, refreshing...');
      
      // Check if refresh token is also expired
      if (!this.refreshToken || this.isTokenExpired(this.refreshToken)) {
        console.log('🚫 Refresh token expired, logging out...');
        this.clearAuthData();
        return null;
      }

      const newToken = await this.refreshAccessToken();
      return newToken;
    }

    return this.accessToken;
  }

  // Check if refresh token is close to expiration (within 1 day)
  isRefreshTokenExpiringSoon(): boolean {
    if (!this.refreshToken) {
      return true;
    }

    try {
      const payload = JSON.parse(atob(this.refreshToken.split('.')[1]));
      const currentTime = Date.now() / 1000;
      const timeUntilExpiry = payload.exp - currentTime;
      const oneDayInSeconds = 24 * 60 * 60;
      
      return timeUntilExpiry < oneDayInSeconds;
    } catch (error) {
      return true; // If we can't parse the token, consider it expired
    }
  }

  // Get access token
  getAccessToken(): string | null {
    return this.accessToken;
  }

  /** JWT payload (access token) — source of truth for impersonation */
  parseAccessTokenPayload(): {
    userId: string;
    email: string;
    role: string;
    impersonatedBy?: string;
  } | null {
    if (!this.accessToken) {
      return null;
    }
    try {
      const base64Payload = this.accessToken.split('.')[1];
      const decodedPayload = this.base64Decode(base64Payload);
      const payload = JSON.parse(decodedPayload);
      return {
        userId: payload.userId,
        email: payload.email,
        role: payload.role,
        ...(payload.impersonatedBy ? { impersonatedBy: payload.impersonatedBy } : {}),
      };
    } catch {
      return null;
    }
  }

  canAccessAdminAnalytics(): boolean {
    const chrome = this.getImpersonationChromeState();
    if (chrome.isImpersonating) {
      return false;
    }
    const role = this.getCurrentUser()?.role;
    return role === 'admin' || role === 'super_admin';
  }

  getImpersonationChromeState(): {
    isImpersonating: boolean;
    isSuperAdmin: boolean;
    /** Barra completa: super admin o sesión de impersonación activa */
    showChrome: boolean;
    /** Puede abrir el listado para elegir otro usuario (sigue visible durante impersonación) */
    canOpenUserPicker: boolean;
  } {
    const p = this.parseAccessTokenPayload();
    if (!p) {
      return {
        isImpersonating: false,
        isSuperAdmin: false,
        showChrome: false,
        canOpenUserPicker: false,
      };
    }
    const isImpersonating = !!p.impersonatedBy;
    const isSuperAdmin = p.role === 'super_admin';
    const canOpenUserPicker = isSuperAdmin || isImpersonating;
    return {
      isImpersonating,
      isSuperAdmin,
      showChrome: canOpenUserPicker,
      canOpenUserPicker,
    };
  }

  async impersonateAs(targetUserId: string): Promise<void> {
    const validToken = await this.getValidAccessToken();
    if (!validToken) {
      throw new Error('Authentication required');
    }

    const response = await fetch(
      `${this.config.BASE_URL}${this.config.ENDPOINTS.AUTH.IMPERSONATE}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${validToken}`,
        },
        body: JSON.stringify({ targetUserId }),
      }
    );

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'Could not start impersonation');
    }

    if (data.success && data.data) {
      analyticsService.stopHeartbeat();
      await this.persistSession(data.data.accessToken, data.data.refreshToken, data.data.user);
      await queryClient.clear();
    }
  }

  async stopImpersonation(): Promise<void> {
    const validToken = await this.getValidAccessToken();
    if (!validToken) {
      throw new Error('Authentication required');
    }

    const response = await fetch(
      `${this.config.BASE_URL}${this.config.ENDPOINTS.AUTH.STOP_IMPERSONATION}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${validToken}`,
        },
      }
    );

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'Could not stop impersonation');
    }

    if (data.success && data.data) {
      analyticsService.stopHeartbeat();
      await this.persistSession(data.data.accessToken, data.data.refreshToken, data.data.user);
      await queryClient.clear();
    }
  }

  private mergeImpersonationFromTokenIntoUserData(): void {
    const p = this.parseAccessTokenPayload();
    if (!p || !this.userData) {
      return;
    }
    if (p.impersonatedBy) {
      this.userData.impersonatedBy = p.impersonatedBy;
    } else {
      delete this.userData.impersonatedBy;
      delete this.userData.impersonatorEmail;
    }
  }

  private async persistSession(
    accessToken: string,
    refreshToken: string,
    user: Record<string, unknown>
  ): Promise<void> {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this.userData = { ...user };
    this.mergeImpersonationFromTokenIntoUserData();
    await this.saveToStorage(STORAGE_KEYS.ACCESS_TOKEN, this.accessToken);
    await this.saveToStorage(STORAGE_KEYS.REFRESH_TOKEN, this.refreshToken);
    await this.saveToStorage(STORAGE_KEYS.USER_DATA, this.userData);
  }

  // Clear auth data
  private async clearAuthData(): Promise<void> {
    this.accessToken = null;
    this.refreshToken = null;
    this.userData = null;

    await this.removeFromStorage(STORAGE_KEYS.ACCESS_TOKEN);
    await this.removeFromStorage(STORAGE_KEYS.REFRESH_TOKEN);
    await this.removeFromStorage(STORAGE_KEYS.USER_DATA);
  }

  // Storage utilities (platform-specific)
  private async saveToStorage(key: string, value: any): Promise<void> {
    try {
      await AsyncStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error saving to storage:', error);
      throw error;
    }
  }

  private async getFromStorage(key: string): Promise<any> {
    try {
      const value = await AsyncStorage.getItem(key);
      return value ? JSON.parse(value) : null;
    } catch (error) {
      console.error('Error getting from storage:', error);
      return null;
    }
  }

  private async removeFromStorage(key: string): Promise<void> {
    try {
      await AsyncStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing from storage:', error);
    }
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService;
